import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigationArrow, MapPin, Check, DotsSixVertical, ArrowCounterClockwise, FloppyDisk, ChatText, Warning } from "@phosphor-icons/react";
import { Reorder } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { stopKey, estimateRoute, arrivalTimes, fmtTime } from "@/lib/routeStops";
import { RouteMap } from "@/components/RouteMap";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function RoutePlanner() {
  const [date, setDate] = useState(todayIso());
  const [events, setEvents] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);
  const loadEvents = () => setReloadTick((t) => t + 1);
  const [origin, setOrigin] = useState("");
  const [opt, setOpt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState("");
  const [appliedCrews, setAppliedCrews] = useState({});
  const [orders, setOrders] = useState({});
  const [savedOrigin, setSavedOrigin] = useState("");
  const [share, setShare] = useState(null);
  const [sharePhone, setSharePhone] = useState("");
  const [sharing, setSharing] = useState(false);
  const [depart, setDepart] = useState("07:00");
  const [onSite, setOnSite] = useState(60);
  const [cutoff, setCutoff] = useState("17:00");
  const [shareLog, setShareLog] = useState({});
  const [custText, setCustText] = useState(null);
  const [custSending, setCustSending] = useState(false);

  const openCustText = (o, eta) => {
    const ev = stops.find((s) => stopKey(s) === o.id);
    const from = new Date(eta.arrive.getTime() - 15 * 60000);
    const to = new Date(eta.arrive.getTime() + 30 * 60000);
    const who = ev?.customer_name ? `Hi ${ev.customer_name.split(" ")[0]}, ` : "Hi, ";
    setCustText({
      job_id: ev?.job_id || "",
      to: ev?.customer_phone || "",
      label: o.label || o.address,
      message: `${who}our crew is scheduled to arrive at ${o.address} between ${fmtTime(from)} and ${fmtTime(to)} on ${new Date(`${date}T12:00:00`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}. Reply here with any questions.`,
    });
  };

  const sendCustText = async () => {
    setCustSending(true);
    try {
      const { data } = await api.post("/route/text-customer", custText);
      toast.success(`ETA texted to ${data.to}`);
      setCustText(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the text");
    }
    setCustSending(false);
  };

  const loadShareLog = useCallback(() => {
    api.get("/route/share-history", { params: { date } })
      .then((r) => setShareLog(r.data || {}))
      .catch(() => setShareLog({}));
  }, [date]);

  useEffect(() => { loadShareLog(); }, [loadShareLog]);

  const fmtSent = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? "" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  useEffect(() => {
    api.get("/route/origin").then((r) => {
      const o = r.data?.origin || "";
      setSavedOrigin(o);
      setOrigin((cur) => cur || o);
    }).catch(() => {});
  }, []);

  const saveOrigin = async () => {
    try {
      await api.put("/route/origin", { origin: origin.trim() });
      setSavedOrigin(origin.trim());
      toast.success(origin.trim() ? "Start point saved as default" : "Default start point cleared");
    } catch (e) {
      toast.error("Couldn't save the start point");
    }
  };

  const openShare = async (c) => {
    setShare(c);
    setSharePhone("");
    try {
      const { data } = await api.get("/route/crew-phone", { params: { crew: c.crew } });
      setSharePhone(data?.phone || "");
    } catch (e) { /* no phone on file */ }
  };

  const sendShare = async () => {
    if (!share) return;
    setSharing(true);
    try {
      const order = crewOrder(share);
      const { data } = await api.post("/route/share", {
        date, crew: share.crew, url: mapsUrl(order), to: sharePhone.trim(), stops: order.length,
      });
      toast.success(`Route texted to ${data.to}`);
      setShareLog((m) => ({ ...m, [share.crew]: { to: data.to, sent_at: data.sent_at } }));
      setShare(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the text");
    }
    setSharing(false);
  };

  const crewOrder = (c) => orders[c.crew] || c.order;
  const isManual = (c) =>
    !!orders[c.crew] && orders[c.crew].some((o, i) => o.id !== c.order[i]?.id);
  const reorder = (c, next) => {
    setOrders((m) => ({ ...m, [c.crew]: next }));
    setAppliedCrews((m) => ({ ...m, [c.crew]: false }));
  };

  useEffect(() => {
    let live = true;
    api
      .get("/jobs/schedule", { params: { month: date.slice(0, 7) } })
      .then((r) => live && setEvents(r.data?.events || []))
      .catch(() => live && setEvents([]));
    return () => { live = false; };
  }, [date, reloadTick]);

  const stops = useMemo(
    () => events.filter((e) => e.date === date && (e.address || "").trim()),
    [events, date],
  );

  const optimize = async () => {
    if (!stops.length) {
      toast.error("No stops with addresses on this day");
      return;
    }
    setBusy(true);
    setOpt(null);
    setAppliedCrews({});
    setOrders({});
    try {
      const { data } = await api.post("/route/optimize", {
        origin: origin.trim(),
        depart: depart ? new Date(`${date}T${depart}:00`).toISOString() : "",
        stops: stops.map((s) => ({
          id: stopKey(s),
          address: s.address || "",
          crew: s.crew || "Unassigned",
          label: s.project_name || "",
          service_min: s.service_min || 0,
        })),
      });
      setOpt(data);
      if (!data.crews?.length) toast.error("Couldn't geocode these addresses");
    } catch (e) {
      toast.error("Route optimization failed");
    }
    setBusy(false);
  };

  const applyOrder = async (c) => {
    setApplying(c.crew);
    try {
      const order = crewOrder(c);
      const etas = arrivalTimes(order, date, depart, onSite, isManual(c));
      await api.post("/route/apply-order", {
        date,
        crew: c.crew,
        order: order.map((o) => o.id),
        etas: Object.fromEntries(order.map((o, i) => [o.id, etas[i]?.arrive?.toISOString() || ""])),
      });
      setAppliedCrews((m) => ({ ...m, [c.crew]: true }));
      toast.success(`${c.crew} route saved to ${date}`);
      loadEvents();
    } catch (e) {
      toast.error("Couldn't save the stop order");
    }
    setApplying("");
  };

  const mapsUrl = (order) => {
    const a = order.map((o) => encodeURIComponent(o.address));
    let u = `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${a[a.length - 1]}`;
    if (origin.trim()) u += `&origin=${encodeURIComponent(origin.trim())}`;
    const wp = a.slice(0, -1).join("|");
    if (wp) u += `&waypoints=${wp}`;
    return u;
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8" data-testid="route-planner">
      <h1 className="text-3xl font-bold text-zinc-100">Route Planner</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Optimize each crew's stops into the fastest driving order, drag to fine-tune, then apply it to the day.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Day
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="route-planner-date"
            className="mt-1 h-11 rounded-xl border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Leave at
          </label>
          <input
            type="time"
            value={depart}
            onChange={(e) => setDepart(e.target.value)}
            data-testid="route-planner-depart"
            title="Departure time — drive times include expected traffic"
            className="mt-1 h-11 rounded-xl border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            On site
          </label>
          <div className="mt-1 flex h-11 items-center rounded-xl border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100">
            <input
              type="number"
              min={0}
              step={15}
              value={onSite}
              onChange={(e) => setOnSite(Math.max(0, Number(e.target.value) || 0))}
              data-testid="route-planner-onsite"
              title="Minutes spent at each stop, used for arrival estimates"
              className="w-14 bg-transparent text-right focus:outline-none"
            />
            <span className="ml-1 text-xs text-zinc-500">min</span>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Day cutoff
          </label>
          <input
            type="time"
            value={cutoff}
            onChange={(e) => setCutoff(e.target.value)}
            data-testid="route-planner-cutoff"
            title="Warn when a crew's day runs past this time"
            className="mt-1 h-11 rounded-xl border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Start point (optional)
          </label>
          <div className="mt-1 flex gap-2">
            <input
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="e.g. shop address"
              data-testid="route-planner-origin"
              className="h-11 w-full rounded-xl border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
            />
            {origin.trim() !== savedOrigin && (
              <button
                type="button"
                onClick={saveOrigin}
                data-testid="route-planner-save-origin"
                title="Remember as default start point"
                className="flex h-11 shrink-0 items-center gap-1 rounded-xl border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500 hover:text-indigo-200"
              >
                <FloppyDisk size={14} weight="bold" /> Save
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={optimize}
          disabled={busy}
          data-testid="route-planner-optimize"
          className="h-11 rounded-xl bg-indigo-500 px-5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-50"
        >
          {busy ? "Optimizing…" : `Optimize ${stops.length} stops`}
        </button>
      </div>

      {opt && !opt.crews?.length && (
        <p className="mt-6 text-sm text-zinc-500">No routable stops for this day.</p>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {opt?.crews?.map((c) => {
          const order = crewOrder(c);
          const manual = isManual(c);
          const stats = manual ? estimateRoute(order) : c;
          const etas = arrivalTimes(order, date, depart, onSite, manual);
          const onSiteTotal = order.reduce(
            (n, o) => n + (o.service_min > 0 ? o.service_min : onSite),
            0,
          );
          const dayMin = (stats.drive_min || 0) + onSiteTotal;
          const doneBy = etas.length ? etas[etas.length - 1].depart : null;
          const cutoffAt = cutoff ? new Date(`${date}T${cutoff}:00`) : null;
          const overbooked = !!(doneBy && cutoffAt && doneBy > cutoffAt);
          return (
          <div
            key={c.crew}
            data-testid={`route-planner-crew-${c.crew}`}
            className="overflow-hidden rounded-2xl border border-indigo-500/30 bg-indigo-500/5"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
              <span className="flex items-center gap-2 text-base font-bold text-zinc-100">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                {c.crew}, {c.stops} stops
              </span>
              {manual ? (
                <button
                  type="button"
                  onClick={() => reorder(c, c.order)}
                  data-testid={`route-planner-reset-${c.crew}`}
                  className="flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 hover:bg-amber-500/25"
                >
                  <ArrowCounterClockwise size={11} weight="bold" /> Manual · reset
                </button>
              ) : (
                <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                  Optimized
                </span>
              )}
            </div>
            <div
              className="flex flex-wrap items-center gap-2 border-b border-white/5 px-4 py-2 text-xs"
              data-testid={`route-planner-day-${c.crew}`}
            >
              <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                Full day
              </span>
              <span
                className="font-mono-data font-bold text-zinc-100"
                data-testid={`route-planner-daytotal-${c.crew}`}
              >
                {Math.floor(dayMin / 60)}h {dayMin % 60}m
                <span className="ml-1 text-[10px] font-normal text-zinc-500">
                  ({stats.drive_min || 0}m drive + {onSiteTotal}m on site)
                </span>
              </span>
              {doneBy && depart && (
                <span
                  className={`ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 font-bold ${
                    overbooked ? "bg-red-500/15 text-red-300" : "bg-indigo-500/15 text-indigo-200"
                  }`}
                  data-testid={`route-planner-doneby-${c.crew}`}
                >
                  {overbooked && <Warning size={11} weight="fill" />}
                  Done by {fmtTime(doneBy)}
                  {overbooked && cutoff ? ` · past ${fmtTime(cutoffAt)}` : ""}
                </span>
              )}
            </div>
            <RouteMap order={order} crew={c.crew} />
            <div className="grid grid-cols-3 divide-x divide-white/5 text-center">
              <div className="py-3">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Distance</div>
                <div className="text-lg font-bold text-indigo-300" data-testid={`route-planner-distance-${c.crew}`}>{stats.distance_mi} mi</div>
              </div>
              <div className="py-3">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                  {opt.traffic && !manual ? "Drive · traffic" : "Drive"}
                </div>
                <div className="text-lg font-bold text-zinc-100" data-testid={`route-planner-drive-${c.crew}`}>
                  {Math.floor(stats.drive_min / 60)}h {stats.drive_min % 60}m
                </div>
                {opt.traffic && !manual && c.base_min != null && c.base_min !== c.drive_min && (
                  <div className="text-[10px] text-amber-300/80">+{c.drive_min - c.base_min}m vs. no traffic</div>
                )}
              </div>
              <div className="py-3">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Saved</div>
                <div className="text-lg font-bold text-emerald-400">{manual ? "—" : `${c.saved_pct}%`}</div>
              </div>
            </div>
            <Reorder.Group
              axis="y"
              values={order}
              onReorder={(next) => reorder(c, next)}
              className="space-y-2 px-4 py-3"
              data-testid={`route-planner-stops-${c.crew}`}
            >
              {order.map((o, i) => (
                <Reorder.Item
                  key={o.id}
                  value={o}
                  data-testid={`route-planner-stop-${c.crew}-${i}`}
                  className="flex cursor-grab items-center gap-2.5 rounded-lg bg-zinc-950/40 px-2 py-1.5 text-sm text-zinc-300 active:cursor-grabbing"
                >
                  <DotsSixVertical size={16} className="shrink-0 text-zinc-600" />
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-zinc-100">
                      {o.label || o.address}
                    </span>
                    <span className="flex items-center gap-1 truncate text-[11px] text-zinc-500">
                      <MapPin size={11} weight="bold" /> {o.address}
                    </span>
                  </span>
                  {etas[i] && (
                    <span
                      className="shrink-0 text-right"
                      data-testid={`route-planner-eta-${c.crew}-${i}`}
                      title="Estimated arrival · leave"
                    >
                      <span className="block text-sm font-bold text-indigo-200">{fmtTime(etas[i].arrive)}</span>
                      <span className="block text-[10px] text-zinc-500">
                        {i === 0 && !origin.trim() ? "start" : `${o.leg_min ?? ""}m drive`}
                        {etas[i].on_site ? ` · ${etas[i].on_site}m on site` : ""} · out {fmtTime(etas[i].depart)}
                      </span>
                    </span>
                  )}
                  {etas[i] && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => openCustText(o, etas[i])}
                      data-testid={`route-planner-text-eta-${c.crew}-${i}`}
                      title="Text this customer their arrival window"
                      className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-700 text-zinc-400 hover:border-indigo-500 hover:text-indigo-200"
                    >
                      <ChatText size={14} weight="bold" />
                    </button>
                  )}
                </Reorder.Item>
              ))}
            </Reorder.Group>
            <div className="mx-4 mb-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => applyOrder(c)}
                disabled={applying === c.crew}
                data-testid={`route-planner-apply-${c.crew}`}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-50 ${
                  appliedCrews[c.crew]
                    ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : "bg-emerald-500 text-white hover:bg-emerald-400"
                }`}
              >
                <Check size={14} weight="bold" />
                {applying === c.crew
                  ? "Saving…"
                  : appliedCrews[c.crew]
                    ? "Order applied"
                    : manual
                      ? "Apply manual order"
                      : "Apply optimized order"}
              </button>
              <a
                href={mapsUrl(order)}
                target="_blank"
                rel="noreferrer"
                data-testid={`route-planner-open-${c.crew}`}
                className="flex items-center justify-center gap-2 rounded-xl border border-indigo-500/40 bg-indigo-500/10 py-2.5 text-[11px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/20"
              >
                <NavigationArrow size={14} weight="bold" /> Google Maps
              </a>
              <button
                type="button"
                onClick={() => openShare(c)}
                data-testid={`route-planner-share-${c.crew}`}
                className="col-span-2 flex items-center justify-center gap-2 rounded-xl border border-zinc-700 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500 hover:text-indigo-200"
              >
                <ChatText size={14} weight="bold" /> Text route to crew lead
              </button>
              {shareLog[c.crew] && (
                <p
                  className="col-span-2 text-center text-[10px] uppercase tracking-widest text-zinc-500"
                  data-testid={`route-planner-last-texted-${c.crew}`}
                >
                  Last texted {fmtSent(shareLog[c.crew].sent_at)} to {shareLog[c.crew].to}
                </p>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {custText && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="route-cust-text-dialog"
          onClick={() => !custSending && setCustText(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-5">
            <h2 className="text-base font-bold text-zinc-100">Text arrival window</h2>
            <p className="mt-1 truncate text-xs text-zinc-500">{custText.label}</p>
            <label className="mt-4 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Customer phone</label>
            {!custText.to && (
              <p className="mt-1 text-[11px] text-amber-300/90" data-testid="route-cust-text-no-phone">No customer phone on this job — enter one.</p>
            )}
            <input
              value={custText.to}
              onChange={(e) => setCustText({ ...custText, to: e.target.value })}
              placeholder="(555) 555-5555"
              data-testid="route-cust-text-phone"
              className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
            />
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Message</label>
            <textarea
              value={custText.message}
              onChange={(e) => setCustText({ ...custText, message: e.target.value })}
              rows={4}
              data-testid="route-cust-text-message"
              className="mt-1 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
            />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCustText(null)} data-testid="route-cust-text-cancel"
                className="rounded-xl border border-zinc-700 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-300">
                Cancel
              </button>
              <button type="button" onClick={sendCustText} disabled={custSending || !custText.to.trim() || !custText.message.trim()}
                data-testid="route-cust-text-send"
                className="rounded-xl bg-indigo-500 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-50">
                {custSending ? "Sending…" : "Send text"}
              </button>
            </div>
          </div>
        </div>
      )}

      {share && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="route-share-dialog"
          onClick={() => !sharing && setShare(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 p-5"
          >
            <h2 className="text-base font-bold text-zinc-100">Text {share.crew} route</h2>
            <p className="mt-1 text-xs text-zinc-500">
              Sends the {crewOrder(share).length}-stop Google Maps link for {date}.
            </p>
            <label className="mt-4 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Crew lead phone
            </label>
            {!sharePhone && (
              <p className="mt-1 text-[11px] text-amber-300/90" data-testid="route-share-no-phone">
                No phone on file for {share.crew}. Add one under{" "}
                <Link to="/crew" className="underline hover:text-amber-200">Crew</Link> so it prefills next time.
              </p>
            )}
            <input
              value={sharePhone}
              onChange={(e) => setSharePhone(e.target.value)}
              placeholder="(555) 555-5555"
              data-testid="route-share-phone"
              className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
            />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setShare(null)}
                data-testid="route-share-cancel"
                className="rounded-xl border border-zinc-700 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendShare}
                disabled={sharing || !sharePhone.trim()}
                data-testid="route-share-send"
                className="rounded-xl bg-indigo-500 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-50"
              >
                {sharing ? "Sending…" : "Send text"}
              </button>
            </div>
          </div>
        </div>
      )}

      {opt && (
        <p className="mt-4 text-[10px] uppercase tracking-widest text-zinc-600">
          {opt.provider === "estimate"
            ? "Estimated · add a Google Maps API key for exact drive times"
            : opt.traffic
              ? `Powered by Google Directions · traffic at ${depart} departure`
              : "Powered by Google Directions · set a future departure for traffic-aware times"}
        </p>
      )}
    </div>
  );
}
